import { Injectable } from '@angular/core';
import { EntityState, EntityStore, StoreConfig } from '@datorama/akita';
import { isEmpty } from 'lodash-es';
import unset from 'lodash-es/unset';

import { OrganizationPropertyName } from 'organization/model/organization-property-name';
import { OrganizationPropertyType } from 'organization/model/organization-property-type';
import { Organization } from 'organization/organization';
import { OrganizationProperty } from 'organization/organization-property';

export interface OrganizationPropertyDocument {
	readonly name: string;
	readonly type: string;
	readonly value: string;
}

export interface OrganizationPriceDocument {
	readonly amount: number;
	readonly currency: string;
}

export interface OrganizationDocument {
	readonly id: string;
	readonly name: string;
	readonly sectors: string;
	readonly iconUrl: string;
	readonly logoUrl: string;
	readonly description: string;
	readonly primaryColor: string;
	readonly updateTimestamp: number;
	readonly shortDescription: string;
	readonly planPrice: OrganizationPriceDocument;
	readonly paymentProviderSubscriptionId: string;
	readonly sessionPrice: OrganizationPriceDocument;
	readonly properties: OrganizationPropertyDocument[];
}

export type OrganizationState = EntityState<OrganizationDocument, string>;

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'organizations' })
export class OrganizationStore extends EntityStore<OrganizationState> {
	constructor() {
		super();
	}

	public asDocument(organization: Organization): OrganizationDocument {
		const document: OrganizationDocument = organization
			? {
					id: organization.id,
					name: organization.name,
					iconUrl: organization.iconUrl,
					logoUrl: organization.logoUrl,
					sectors: organization.sectors,
					primaryColor: organization.primaryColor,
					planPrice: organization.planPrice,
					description: organization.description,
					sessionPrice: organization.sessionPrice,
					updateTimestamp: organization.updateTimestamp,
					shortDescription: organization.shortDescription,
					properties: organization.properties.map(
						(p) =>
							({
								name: p.name,
								type: p.type,
								value: p.value,
							} as OrganizationPropertyDocument)
					),
					paymentProviderSubscriptionId: organization.paymentProviderSubscriptionId,
			  }
			: null;

		if (!document.description) {
			unset(document, 'description');
		}

		if (isEmpty(document.properties)) {
			unset(document, 'properties');
		}

		return document;
	}

	public asEntity(document: OrganizationDocument): Organization {
		return document
			? new Organization(
					document.id,
					document.name,
					document.sectors,
					document.iconUrl,
					document.logoUrl,
					document.description,
					document.primaryColor,
					document.planPrice,
					document.updateTimestamp,
					document.shortDescription,
					document.sessionPrice,
					(document.properties || []).map(
						(property) =>
							new OrganizationProperty(
								OrganizationPropertyName[property.name],
								OrganizationPropertyType[property.type],
								property.value
							)
					),
					document.paymentProviderSubscriptionId
			  )
			: null;
	}
}
